import React from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { cn } from "../../../lib/utils";
import { Flag, TrendingUp, PieChart, Activity, AlertCircle, CheckCircle2 } from "lucide-react";

const OverviewTab: React.FC = () => {
    return (
        <div className="px-4 space-y-6 mt-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="p-4 bg-card rounded-2xl border border-border shadow-sm flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <Flag className="w-3 h-3 text-primary" />
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Goal</span>
                    </div>
                    <div className="font-extrabold text-lg">$2.5M</div>
                </Card>
                <Card className="p-4 bg-card rounded-2xl border border-border shadow-sm flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">ROI</span>
                    </div>
                    <div className="font-extrabold text-lg text-emerald-600 dark:text-emerald-400">12%</div>
                </Card>
                <Card className="p-4 bg-card rounded-2xl border border-border shadow-sm flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <PieChart className="w-3 h-3 text-blue-500" />
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Funding</span>
                    </div>
                    <div className="font-extrabold text-lg">Seed</div>
                </Card>
                <Card className="p-4 bg-card rounded-2xl border border-border shadow-sm flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <Activity className="w-3 h-3 text-indigo-500" />
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Progress</span>
                    </div>
                    <div className="font-extrabold text-lg">85%</div>
                </Card>
            </div>

            {/* Problem & Solution */}
            <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-card rounded-xl p-5 border-l-4 border-l-primary shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-md text-red-600 dark:text-red-400">
                            <AlertCircle className="w-4 h-4" />
                        </div>
                        <h3 className="font-bold">The Problem</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Regional energy grids suffer from consistent outages due to aging infrastructure, leading to a 15% annual loss in industrial productivity.
                    </p>
                </div>
                <div className="bg-card rounded-xl p-5 border-l-4 border-l-emerald-500 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-md text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-4 h-4" />
                        </div>
                        <h3 className="font-bold">The Solution</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Quantum Solar provides a decentralized 50MW solar array with battery storage, ensuring 99.9% uptime for the adjacent industrial zone.
                    </p>
                </div>
            </div>

            {/* About Section */}
            <Card className="p-6 space-y-4">
                <h3 className="font-bold text-lg">About the Project</h3>
                <div className="space-y-3">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Quantum Solar is a flagship renewable energy venture aimed at stabilizing the energy infrastructure of rapidly growing industrial corridors. By leveraging cutting-edge bifacial solar panels and integrated lithium-ion storage systems, the project addresses the critical gap between energy demand and grid reliability.
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Our approach focuses on decentralized power generation, reducing transmission losses and providing a sustainable, cost-effective alternative to traditional fossil fuel-based backup systems used by local industries.
                    </p>
                </div>
                <div className="pt-2">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3">Key Highlights</h4>
                    <ul className="space-y-3">
                        {[
                            "50MW capacity with scalable modular architecture",
                            "Guaranteed 20-year Power Purchase Agreement (PPA)",
                            "Strategic proximity to 12 major manufacturing plants",
                            "AI-driven energy management and distribution system"
                        ].map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5" />
                                <span className="text-sm">{highlight}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Card>

            {/* Timeline */}
            <Card className="p-6">
                <div className="flex justify-between items-end mb-8">
                    <h3 className="font-bold text-lg">Development Stage</h3>
                    <Badge className="bg-primary/10 text-primary border-none hover:bg-primary/20 uppercase tracking-wider text-[10px]">On Track</Badge>
                </div>
                <div className="relative pl-6 border-l-2 border-border space-y-8 ml-2">
                    {[
                        { title: "Land Acquisition", status: "Completed Jan 2023", completed: true },
                        { title: "Permitting & Licensing", status: "Completed Jun 2023", completed: true },
                        { title: "Construction Phase 1", status: "In Progress (85%)", current: true, note: "Currently installing panel arrays in Sector B. Grid connection scheduled for next month." },
                        { title: "Grid Connection", status: "Est. Dec 2023", future: true }
                    ].map((item, idx) => (
                        <div key={idx} className="relative">
                            <div className={cn(
                                "absolute -left-[31px] top-1 h-3.5 w-3.5 rounded-full ring-4 ring-background",
                                item.completed ? "bg-primary" : item.current ? "bg-emerald-500 animate-pulse" : "bg-muted"
                            )} />
                            <h4 className={cn("text-sm font-bold", item.future && "font-medium text-muted-foreground")}>{item.title}</h4>
                            <p className={cn("text-xs mt-0.5", item.current ? "text-emerald-600 dark:text-emerald-400 font-bold" : "text-muted-foreground")}>
                                {item.status}
                            </p>
                            {item.note && (
                                <div className="text-xs text-muted-foreground mt-3 bg-muted/50 p-3 rounded-xl border border-border leading-relaxed">
                                    {item.note}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
};

export default OverviewTab;
